/**
 * Workflow Builder API Service
 *
 * Handles all API calls for the workflow builder feature:
 * - CRUD operations for workflows
 * - Version management
 * - AI suggestions
 * - Component library
 */

import { ApiClient } from './api';

/**
 * WorkflowBuilder Service for AI-native workflow design
 */
export class WorkflowBuilderService {
  constructor(getToken) {
    this.client = new ApiClient(getToken);
  }

  // ============================================
  // WORKFLOW CRUD
  // ============================================

  /**
   * List all workflows with optional filters
   * @param {Object} params - Query parameters
   * @param {Object} options - Request options
   */
  async list(params = {}, options = {}) {
    const query = new URLSearchParams();
    if (params.workflow_type) query.append('workflow_type', params.workflow_type);
    if (params.status) query.append('status', params.status);
    if (params.search) query.append('search', params.search);
    if (params.limit) query.append('limit', params.limit);
    if (params.offset) query.append('offset', params.offset);
    if (params.sort_by) query.append('sort_by', params.sort_by);
    if (params.sort_order) query.append('sort_order', params.sort_order);

    const queryString = query.toString();
    return this.client.get(
      `/workflow-builder/workflows${queryString ? `?${queryString}` : ''}`,
      options
    );
  }

  /**
   * Create a new workflow
   * @param {Object} data - Workflow data
   * @param {Object} options - Request options
   */
  async create(data, options = {}) {
    return this.client.post('/workflow-builder/workflows', data, options);
  }

  /**
   * Generate a shareable, hosted verification link (/v/{token}) that runs a
   * published workflow as the applicant sees it. Backed by /verify/create-session,
   * which resolves the workflow by name and derives the tenant from it.
   * @param {string} workflowName - Exact published workflow name
   * @param {Object} opts - { label, expiresHours }
   */
  async createLiveLink(workflowName, opts = {}, options = {}) {
    return this.client.post(
      '/verify/create-session',
      {
        label: opts.label || 'live',
        expires_hours: opts.expiresHours || 168,
        workflow_name: workflowName,
      },
      options
    );
  }

  /**
   * Get a workflow by ID with all versions
   * @param {string} id - Workflow ID
   * @param {Object} options - Request options
   */
  async get(id, options = {}) {
    return this.client.get(`/workflow-builder/workflows/${id}`, options);
  }

  /**
   * Update a workflow
   * @param {string} id - Workflow ID
   * @param {Object} data - Update data
   * @param {Object} options - Request options
   */
  async update(id, data, options = {}) {
    return this.client.put(`/workflow-builder/workflows/${id}`, data, options);
  }

  /**
   * Delete a workflow
   * @param {string} id - Workflow ID
   * @param {Object} options - Request options
   */
  async delete(id, options = {}) {
    return this.client.delete(`/workflow-builder/workflows/${id}`, options);
  }

  /**
   * Unpublish a workflow (revert to draft)
   * @param {string} id - Workflow ID
   * @param {Object} options - Request options
   */
  async unpublish(id, options = {}) {
    return this.client.post(`/workflow-builder/workflows/${id}/unpublish`, {}, options);
  }

  // ============================================
  // VERSION MANAGEMENT
  // ============================================

  /**
   * List all versions of a workflow
   * @param {string} workflowId - Workflow ID
   * @param {Object} options - Request options
   */
  async listVersions(workflowId, options = {}) {
    return this.client.get(
      `/workflow-builder/workflows/${workflowId}/versions`,
      options
    );
  }

  /**
   * Create a new version
   * @param {string} workflowId - Workflow ID
   * @param {Object} data - Version data
   * @param {Object} options - Request options
   */
  async createVersion(workflowId, data, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/versions`,
      data,
      options
    );
  }

  /**
   * Publish a workflow version
   * @param {string} workflowId - Workflow ID
   * @param {Object} data - Publish data (version_id, release_notes)
   * @param {Object} options - Request options
   */
  async publish(workflowId, data = {}, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/publish`,
      data,
      options
    );
  }

  /**
   * Rollback to a previous version
   * @param {string} workflowId - Workflow ID
   * @param {string} versionId - Version ID to rollback to
   * @param {Object} options - Request options
   */
  async rollback(workflowId, versionId, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/rollback`,
      { version_id: versionId },
      options
    );
  }

  /**
   * Preview a workflow (test without publishing)
   * @param {string} workflowId - Workflow ID
   * @param {Object} data - Preview options (use_current_config)
   * @param {Object} options - Request options
   */
  async preview(workflowId, data = { use_current_config: true }, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/preview`,
      data,
      options
    );
  }

  /**
   * Get diff between two versions
   * @param {string} workflowId - Workflow ID
   * @param {string} versionId - Version ID
   * @param {string} compareToId - Version ID to compare against
   * @param {Object} options - Request options
   */
  async getVersionDiff(workflowId, versionId, compareToId, options = {}) {
    return this.client.get(
      `/workflow-builder/workflows/${workflowId}/versions/${versionId}/diff?compare_to=${compareToId}`,
      options
    );
  }

  // ============================================
  // AI INTERFACE
  // ============================================

  /**
   * Get AI suggestion for workflow modification
   * @param {string} workflowId - Workflow ID
   * @param {Object} data - { message, current_config, context }
   * @param {Object} options - Request options
   */
  async getAISuggestion(workflowId, data, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/ai/suggest`,
      data,
      options
    );
  }

  /**
   * Apply an AI suggestion
   * @param {string} workflowId - Workflow ID
   * @param {Object} data - { suggestion, updated_config }
   * @param {Object} options - Request options
   */
  async applyAISuggestion(workflowId, data, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/ai/apply`,
      data,
      options
    );
  }

  /**
   * Validate a workflow configuration
   * @param {string} workflowId - Workflow ID
   * @param {Object} data - { config }
   * @param {Object} options - Request options
   */
  async validate(workflowId, data, options = {}) {
    return this.client.post(
      `/workflow-builder/workflows/${workflowId}/validate`,
      data,
      options
    );
  }

  // ============================================
  // COMPONENT LIBRARY
  // ============================================

  /**
   * List all available components
   * @param {Object} options - Request options
   */
  async listComponents(options = {}) {
    return this.client.get('/workflow-builder/components', options);
  }

  /**
   * Get a specific component
   * @param {string} componentId - Component ID
   * @param {Object} options - Request options
   */
  async getComponent(componentId, options = {}) {
    return this.client.get(
      `/workflow-builder/components/${componentId}`,
      options
    );
  }

  // ============================================
  // NATURAL LANGUAGE CHAT INTERFACE
  // ============================================

  /**
   * Chat with AI to create/modify workflows
   * @param {Object} data - { message, conversation_id, workflow_id, context }
   * @param {Object} options - Request options
   */
  async chat(data, options = {}) {
    return this.client.post('/workflow-builder/ai/chat', data, options);
  }

  /**
   * Parse policy document into workflow
   * @param {Object} data - { policy_text, industry, region }
   * @param {Object} options - Request options
   */
  async parsePolicy(data, options = {}) {
    return this.client.post('/workflow-builder/ai/parse-policy', data, options);
  }

  /**
   * Upload a policy file (PDF/DOCX/TXT/MD), extract text, and parse into workflow
   * @param {File} file - The file to upload
   * @param {Object} params - { industry, region, save_policy }
   * @param {Object} options - Request options
   */
  async uploadPolicyFile(file, params = {}, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (params.industry) formData.append('industry', params.industry);
    if (params.region) formData.append('region', params.region);
    formData.append('save_policy', params.save_policy !== false ? 'true' : 'false');

    // Note: Don't set Content-Type header for FormData - browser sets it automatically with boundary.
    // MUST use postForm (not post) - post() JSON.stringifies the body, turning FormData into "{}".
    return this.client.postForm('/workflow-builder/ai/upload-policy', formData, options);
  }

  /**
   * Extract structured requirements from a policy file (Step 1 of 2-step flow)
   * @param {File} file - The file to upload
   * @param {Object} params - { industry, region }
   * @param {Object} options - Request options
   */
  async extractRequirements(file, params = {}, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    if (params.industry) formData.append('industry', params.industry);
    if (params.region) formData.append('region', params.region);

    // Note: Don't set Content-Type header for FormData - browser sets it automatically with boundary.
    // MUST use postForm (not post) - post() JSON.stringifies the body, turning FormData into "{}".
    return this.client.postForm('/workflow-builder/ai/extract-requirements', formData, options);
  }

  /**
   * Generate workflow from user-confirmed requirements (Step 2 of 2-step flow)
   * @param {Object} data - { requirements, industry, region }
   * @param {Object} options - Request options
   */
  async generateFromRequirements(data, options = {}) {
    return this.client.post('/workflow-builder/ai/generate-from-requirements', data, options);
  }

  /**
   * Migrate a competitor workflow from a screenshot using AI vision
   * @param {File} imageFile - Screenshot image (PNG, JPEG, GIF, WebP)
   * @param {string} explanation - Explanation of what the workflow does
   * @param {string} sourcePlatform - Optional hint about source platform
   * @param {Object} options - Request options
   */
  async migrateFromScreenshot(imageFile, explanation, sourcePlatform = null, options = {}) {
    const formData = new FormData();
    formData.append('file', imageFile);
    formData.append('explanation', explanation);
    if (sourcePlatform) {
      formData.append('source_platform', sourcePlatform);
    }

    // Note: Don't set Content-Type header for FormData - browser sets it automatically with boundary.
    // MUST use postForm (not post) - post() JSON.stringifies the body, turning FormData into "{}".
    return this.client.postForm('/workflow-builder/ai/migrate-from-screenshot', formData, options);
  }

  /**
   * List stored policies for the current tenant
   * @param {Object} params - { status }
   */
  async listPolicies(params = {}) {
    const query = new URLSearchParams();
    if (params.status) query.set('status', params.status);
    const qs = query.toString();
    return this.client.get(`/workflow-builder/policies${qs ? '?' + qs : ''}`);
  }

  /**
   * Delete (archive) a stored policy
   * @param {string} policyId
   */
  async deletePolicy(policyId) {
    return this.client.delete(`/workflow-builder/policies/${policyId}`);
  }

  // ============================================
  // EXECUTIONS
  // ============================================

  /**
   * List workflow executions
   * @param {Object} params - { workflow_id, applicant_id, status, execution_mode, limit, offset }
   */
  async listExecutions(params = {}) {
    const query = new URLSearchParams();
    if (params.workflow_id) query.set('workflow_id', params.workflow_id);
    if (params.applicant_id) query.set('applicant_id', params.applicant_id);
    if (params.status) query.set('status', params.status);
    if (params.execution_mode) query.set('execution_mode', params.execution_mode);
    if (params.limit) query.set('limit', params.limit);
    if (params.offset) query.set('offset', params.offset);
    const qs = query.toString();
    return this.client.get(`/workflow-builder/executions${qs ? `?${qs}` : ''}`);
  }

  /**
   * Get execution details
   * @param {string} executionId - Execution ID
   */
  async getExecution(executionId) {
    return this.client.get(`/workflow-builder/executions/${executionId}`);
  }

  /**
   * Get execution events (audit trail)
   * @param {string} executionId - Execution ID
   */
  async getExecutionEvents(executionId) {
    return this.client.get(`/workflow-builder/executions/${executionId}/events`);
  }

  // ============================================
  // CUSTOM COMPONENTS
  // ============================================

  /**
   * Create a custom component for your tenant
   * @param {Object} data - Component definition
   * @param {Object} options - Request options
   */
  async createComponent(data, options = {}) {
    return this.client.post('/workflow-builder/components', data, options);
  }

  /**
   * Update a custom component
   * @param {string} componentId - Component ID
   * @param {Object} data - Update data
   * @param {Object} options - Request options
   */
  async updateComponent(componentId, data, options = {}) {
    return this.client.put(
      `/workflow-builder/components/${componentId}`,
      data,
      options
    );
  }

  /**
   * Delete a custom component
   * @param {string} componentId - Component ID
   * @param {Object} options - Request options
   */
  async deleteComponent(componentId, options = {}) {
    return this.client.delete(
      `/workflow-builder/components/${componentId}`,
      options
    );
  }

  // ============================================
  // TEMPLATES
  // ============================================

  /**
   * List available workflow templates
   * @param {Object} params - { category, industry, workflow_type }
   * @param {Object} options - Request options
   */
  async listTemplates(params = {}, options = {}) {
    const query = new URLSearchParams();
    if (params.category) query.set('category', params.category);
    if (params.industry) query.set('industry', params.industry);
    if (params.workflow_type) query.set('workflow_type', params.workflow_type);
    const qs = query.toString();
    return this.client.get(`/workflow-builder/templates${qs ? `?${qs}` : ''}`, options);
  }

  /**
   * Get a specific template
   * @param {string} templateId - Template ID
   * @param {Object} options - Request options
   */
  async getTemplate(templateId, options = {}) {
    return this.client.get(`/workflow-builder/templates/${templateId}`, options);
  }

  /**
   * Create a workflow from a template
   * @param {string} templateId - Template ID
   * @param {Object} data - { name, description }
   * @param {Object} options - Request options
   */
  async createFromTemplate(templateId, data, options = {}) {
    return this.client.post(
      `/workflow-builder/templates/${templateId}/create`,
      data,
      options
    );
  }
}
